import Analytics from 'analytics';
import googleTagManager from '@analytics/google-tag-manager';
import mixpanelPlugin from '@analytics/mixpanel';
import TabIdCoordinator from './util/TabIdCoordinator';
import { merge } from 'lodash';
import {
  identifyMixpanelFromDeviceIdCookie,
  syncMixpanelDeviceIdCookie,
} from './util/mixpanelDeviceId';
import { onLCP } from 'web-vitals';

const RESOLVE_IP_TIMEOUT = 3_000;
const LCP_TIMEOUT = 8_000;

function enrichWithIp(userConfig) {
  let ip = 'Unknown';
  let isLoaded = false;
  /** @type number */
  let ipFetchStart;

  const addIpToProperties = ({ payload }) => {
    if (typeof window === `undefined`) { return; }

    return merge(payload, {
      properties: {
        userIp: ip,
      },
    });
  };

  // return object for analytics to use
  return {
    /* All plugins require a name */
    name: 'enrich-with-ip',
    initialize: () => {
      if (typeof window === `undefined`) { return; }

      ipFetchStart = Date.now();
      fetch(
        'https://api.minware.com/api/ip',
        { signal: AbortSignal.timeout(5000) },
      )
        .then(response => response.json())
        .then(ipResponse => {
          ip = ipResponse.ip;
        })
        .catch(e => {
          window.newrelic?.noticeError(e);
        })
        .finally(() => {
          isLoaded = true;
        });
    },
    loaded: () => {
      return isLoaded || (Date.now() - ipFetchStart > RESOLVE_IP_TIMEOUT);
    },
    pageStart: addIpToProperties,
    trackStart: addIpToProperties,
    identifyStart: addIpToProperties,
  };
}

function enrichWithPageLoadId(userConfig) {
  let pageLoadId = null;

  const addPageLoadIdToProperties = ({ payload }) => {
    if (typeof window === `undefined`) { return; }

    return merge(payload, {
      properties: {
        pageLoadId: pageLoadId,
      },
    });
  };

  return {
    name: 'enrich-with-page-load-id',
    initialize: () => {
      pageLoadId = getPageLoadId();
    },
    'page:mixpanel': addPageLoadIdToProperties,
    'track:mixpanel': addPageLoadIdToProperties,
  };
}

function enrichWithTabId(userConfig) {
  let tabIdCoordinator = null;

  const addTabIdToProperties = ({ payload }) => {
    if (typeof window === `undefined`) { return; }

    return merge(payload, {
      properties: {
        tabId: tabIdCoordinator.tabId,
      },
    });
  };

  return {
    name: 'enrich-with-tab-id',
    initialize: () => {
      if (typeof window === `undefined`) { return; }
      tabIdCoordinator = new TabIdCoordinator();
    },
    'page:mixpanel': addTabIdToProperties,
    'track:mixpanel': addTabIdToProperties,
  };
}

function waitForLCPPlugin(userConfig) {
  let isReady = false;
  return {
    name: 'waitForLCPPlugin',
    /* Hook into initializeStart. This is before third party scripts have loaded on the page */
    async initializeStart({ abort, config }) {
      if (typeof window === `undefined`) { return; }

      return Promise.race([
        new Promise(resolve => {
          onLCP(resolve);
        }),
        new Promise(resolve => {
          setTimeout(resolve, LCP_TIMEOUT);
        }),
      ]).then(() => {
        isReady = true;
      });
    },
    loaded() {
      return isReady;
    },
  };
}

const waitForDocumentReadyPlugin = {
  name: 'waitForDocumentReady',
  /* Hook into initializeStart. This is before third party scripts have loaded on the page */
  async initializeStart({ abort, config }) {
    if (
      typeof window !== 'undefined' && typeof document !== 'undefined'
      && document.readyState !== 'complete'
    ) {
      return new Promise(resolve => {
        window.addEventListener('load', () => {
          resolve(true)
        });
      });
    }
    return true;
  },
  loaded() {
    return typeof document !== 'undefined' && document.readyState === 'complete';
  },
};

const analytics = Analytics({
  app: 'minware-www',
  /** enable this to see analytics in the redux devtools */
  // debug: true,
  plugins: [
    waitForLCPPlugin(),
    waitForDocumentReadyPlugin,
    enrichWithIp(),
    enrichWithPageLoadId(),
    enrichWithTabId(),
    // These are for production - TODO: use different tokens for staging/dev
    googleTagManager({
      containerId: 'GTM-W7VWQDQ',
    }),
    mixpanelPlugin({
      token: '7998a44309da6bee088d922e72d2eef3',
      customScriptSrc: 'https://track.minware.com/prx/mp/lib.min.js',
      options: {
        api_host: 'https://track.minware.com/prx/mp',
        ignore_dnt: true,
        record_sessions_percent: 1,
        record_heatmap_data: true,
        record_mask_text_selector: 'input[type="password"]',
        batch_requests: false,
        loaded: (mixpanel) => {
          identifyMixpanelFromDeviceIdCookie(mixpanel);
          syncMixpanelDeviceIdCookie(mixpanel);
        },
        // Use localStorage, otherwise the cookie can get cleared during the login flow at
        // auth.minware.com because it may move the storage from the cookie into localStorage, which
        // clears the cookie on www as well, but then www doesn't have the local storage.
        persistence: 'localStorage',
      },
    }),
  ],
});

// Set to global so analytics plugin will work with Gatsby
if (typeof window !== 'undefined') {
  window.Analytics = analytics;
}

/**
 * Generate a random page load id for tracking purposes
 */
let pageLoadId = null;
function getPageLoadId() {
  if (pageLoadId) {
    return pageLoadId;
  }
  pageLoadId = `X-${new Date().toISOString()}-${Math.floor(Math.random() * 10 ** 16)}`;
  return pageLoadId;
}

const getCurrentTime = () => {
  // Get current time in seconds. This is the same format mixpanel uses
  return Date.now() / 1000;
};

export function track(eventName, payload = {}, options, cb) {
  // Uncomment for local testing
  // console.log('track', eventName, payload, options, cb);
  analytics.ready(() => {
    // Add trackedAt to payload
    const newPayload = {
      ...(payload || {}),
      trackedAt: getCurrentTime(),
    };
    analytics.track(eventName, newPayload, options, cb);
  });
}

export function page(pageData = {}, options, cb) {
  analytics.ready(() => {
    // Add trackedAt to pageData
    const newPageData = {
      ...(pageData || {}),
      trackedAt: getCurrentTime(),
    };
    analytics.page(newPageData, options, cb);
  });
}

export function trackError(error, properties = {}) {
  window.newrelic?.noticeError(error, properties);
}

/**
 * Setup page lifecycle tracking
 */
function setupPageLifecycleTracking() {
  if (typeof document === 'undefined') { return; }

  /**
   * Track page unload
   */
  document.addEventListener('beforeunload', () => {
    track('Page Unload');
  });

  /**
   * Track page visibility change
   */
  document.addEventListener('visibilitychange', () => {
    track(document.visibilityState === 'visible' ? 'Page Became Visible' : 'Page Became Hidden');
  });
}

setupPageLifecycleTracking();

// Export for consumption in your components for .track & .identify calls
export default analytics;
// We need to do this because we wrap our calls in ready and if ready isn't called before the ready
// event is fired the internal state in the analytics packages is not updated
analytics.ready(() => {
  syncMixpanelDeviceIdCookie();
});
